/**
 * Matemática de multiplicación del plinko (lógica pura, sin Phaser/Matter).
 *
 * Cada bola visual porta un `weight` (bolas lógicas que representa, ver
 * `weights.ts`) y acumula una lista de cruces de barra en el orden en que
 * los toca camino al embudo. Este módulo resuelve cuánto aporta esa bola al
 * contador final, y cómo se suman todas las bolas de un vertido.
 */

import type { BarKind } from '../../core/types';
import { NEGATIVE_FACTOR } from './constants';

/** Un cruce de barra resuelto, ya con el valor final que corresponde aplicar. */
export interface BarCrossing {
  kind: BarKind;
  /**
   * Valor multiplicador ya resuelto. Obligatorio para 'mult' (x2..x7) y
   * 'mystery' (valor revelado 2..7). Se ignora en 'trampoline' y 'negative',
   * que tienen un factor fijo.
   */
  value?: number;
}

/** Factor multiplicativo que aporta un único cruce de barra. */
export function factorForCrossing(crossing: BarCrossing): number {
  switch (crossing.kind) {
    case 'mult':
    case 'mystery':
      if (crossing.value === undefined) {
        throw new Error(`factorForCrossing: falta 'value' para una barra de tipo '${crossing.kind}'`);
      }
      return crossing.value;
    case 'negative':
      // "resta 25% del contador al cruzar" == multiplica lo que porta la bola por 0.75.
      return NEGATIVE_FACTOR;
    case 'trampoline':
      // El trampolín solo da impulso físico (más cruces posibles); no altera el valor.
      return 1;
    default: {
      const exhaustive: never = crossing.kind;
      throw new Error(`factorForCrossing: tipo de barra desconocido '${String(exhaustive)}'`);
    }
  }
}

/**
 * Valor final (bolas lógicas) que aporta una bola visual al contador, tras
 * cruzar cero o más barras en orden. Los factores se encadenan
 * multiplicativamente: cruzar x3 y luego x2 multiplica por 6 en total.
 */
export function computeBallContribution(weight: number, crossings: readonly BarCrossing[]): number {
  const factor = crossings.reduce((acc, crossing) => acc * factorForCrossing(crossing), 1);
  return weight * factor;
}

/** Contribución en crudo de una bola visual, antes de sumar el total del vertido. */
export interface BallContribution {
  weight: number;
  crossings: readonly BarCrossing[];
}

/**
 * Suma las contribuciones de todas las bolas visuales de un vertido y
 * redondea UNA sola vez al final (no bola por bola), para minimizar el
 * error de redondeo acumulado. El resultado nunca es negativo: aunque el
 * tablero esté lleno de barras negativas, no se puede "deber" bolas.
 */
export function computePourTotal(contributions: readonly BallContribution[]): number {
  const total = contributions.reduce(
    (sum, c) => sum + computeBallContribution(c.weight, c.crossings),
    0
  );
  return Math.max(0, Math.round(total));
}
